<template>
  <div v-if="plan" class="plan-card">
    <slot name="before"></slot>
    <h2>{{ plan.label }}</h2>
    <div class="plan-card__sublabel">{{ plan.sublabel }}</div>

    <slot>
      <div v-if="showPriceArea" class="plan-card__pricearea">
        <div class="plan-card__content">
          <span v-if="plan.canAddSeats">
            <input-number
              v-model:value="users"
              :min="plan.minSeats ?? 1"
              @update:value="emit('update:users', $event)"
            />
            {{ $t("words.user", users) }}
          </span>
          <span v-else> {{ plan.minSeats }} {{ $t("words.user", 1) }} </span
          ><span v-if="plan.subscriptionType == 'organization'"
            >,
            {{
              users
                ? `${formatNumber(users)} ${$t("objectNames.workspace", users ?? 100)}`
                : $t("texts.unlimitedTeams")
            }}
          </span>
        </div>
        <div v-if="plan.priceOverride" class="plan-card__price">
          {{ plan.priceOverride }}
        </div>
        <div
          v-else-if="plan.canBeOrderedDirectly == false"
          class="plan-card__price"
        >
          {{ $t("labels.onRequest") }}
        </div>
        <template v-else>
          <div class="plan-card__price">{{ formattedPrice }}&hairsp;€</div>
          <div class="plan-card__pricedetails">
            {{ $t(`per.${paymentPeriod}`) }},
            {{ $t("labels.taxesNotIncluded") }}
          </div>
        </template>
      </div>
    </slot>

    <a
      v-if="plan.buttonOnclickOverride"
      :href="plan.buttonOnclickOverride"
      class="button plan-card__button"
    >
      {{ plan.buttonOverride ?? $t("actions.choose") }}
    </a>
    <button
      v-else-if="showPriceArea"
      type="button"
      class="button plan-card__button"
      :disabled="disabled"
      @click.prevent="selectPlan()"
    >
      {{ plan.buttonOverride ?? $t("actions.choose") }}
    </button>

    <formatted-text
      :value="formattedDescription"
      class="plan-card__description"
    ></formatted-text>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { formatNumber } from "@/helpers/utility/Formatting";

import FormattedText from "@core/ui/FormattedText.vue";
import InputNumber from "@core/input/InputNumber.vue";

import { usePlans } from "@manager/plans/plans.store";
import type { LicensePlan } from "@manager/plans/plans.store";

const props = withDefaults(
  defineProps<{
    planId: string;
    users?: number;
    showPriceArea?: boolean;
    disabled?: boolean;
    paymentPeriod?: "year" | "month";
  }>(),
  {
    showPriceArea: false,
    disabled: false,
    paymentPeriod: "year"
  }
);

const emit = defineEmits<{
  (e: "buy", plan: LicensePlan): void;
  (e: "update:users", users: number): void;
}>();

const { plans } = usePlans();

const users = ref(props.users ?? 1);

const plan = computed(() => {
  return plans[props.planId];
});

watch(
  plan,
  () => {
    users.value = plan.value?.minSeats ?? 1;
  },
  { immediate: true }
);
watch(
  () => props.users,
  (value) => {
    if (value != null) {
      users.value = Math.max(value, plan.value?.minSeats ?? 1);
    }
  }, { immediate: true }
);

const formattedPrice = computed(() => {
  if (plan.value == null) return "";
  if (plan.value.price == null) return "";

  const pricePerPeriod = plan.value.price;

  if (!plan.value.canAddSeats) return pricePerPeriod;

  return pricePerPeriod * users.value;
});

const formattedDescription = computed(() => {
  if (plan.value?.description == null) return "";
  const parts = plan.value.description.split("\n");

  let formatted = "";
  let inList = false;
  for (let part of parts) {
    if (part.startsWith("* ")) {
      if (!inList) {
        formatted += "<ul>";
        inList = true;
      }
      part = `<li>${part.replace("* ", "")}</li>`;
    } else {
      if (inList) {
        formatted += "</ul>";
        inList = false;
      }
      if (part.startsWith("# ")) {
        part = `<h3>${part.replace("# ", "")}</h3>`;
      } else {
        part = `<p>${part}</p>`;
      }
    }
    part = part.replaceAll(/\*([^\*]+)\*/g, "<strong>$1</strong>");
    formatted += part;
  }
  return formatted;
});

function selectPlan() {
  emit("update:users", users.value);
  emit("buy", plan.value);
}
</script>

<style lang="scss">
.plan-card {
  padding-top: 1.5rem;
  padding-inline: 0;
  background-color: var(--color-background-deep);
  border-radius: 0.5rem;
  box-shadow: 0 4px 10px var(--color-shadow-1);
  position: relative;
  overflow: hidden;

  h2 {
    color: var(--color-action);
    font-size: 2rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    padding-inline: 1.5rem;
  }

  &__current {
    position: absolute;
    top: 0;
    right: 0;
    padding: 0.25rem 0.75rem;
    background-color: var(--color-action);
    color: var(--color-action-contrast);
    border-bottom-left-radius: 5px;
  }

  &__sublabel {
    padding-inline: 1.5rem;
    margin-bottom: 1rem;
    min-height: 3rem;
  }

  &__pricearea {
    min-height: 7rem;
    padding: 1rem 1.5rem;
  }
  &__content {
    color: var(--color-text-moderate);
    margin-bottom: 0.25rem;
    height: 2rem;
    line-height: 1.5;

    .input__container {
      display: inline-block;
      width: 4.5rem;
      margin-right: 0.25rem;
    }
  }
  &__price {
    font-weight: 700;
    font-size: 2.5rem;
    margin-bottom: 0.25rem;
  }
  &__pricedetails {
    color: var(--color-text-medium);
  }

  &__button {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }

  &__description {
    height: 100%;
    margin-top: 1rem;
    padding-inline: 1.5rem;
    padding-top: 1em;
    padding-bottom: 1.5em;
    background-color: var(--color-background);
    font-size: 16px;

    ul {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding-left: 0;
      list-style: none;
    }
    li {
      position: relative;
      padding-left: 1.75em;
      padding-block: 0.3em;
      p {
        margin: 0;
        text-wrap: balance;
      }
      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0.6em;
        display: inline-block;
        width: 0.9em;
        height: 0.9em;
        border-radius: 50%;
        border: 1px solid var(--color-action);
        background-color: var(--color-primary-light);
        opacity: 0.8;
      }
      &::after {
        content: "";
        position: absolute;
        left: 0.3em;
        top: 0.9em;
        display: inline-block;
        width: 0.4em;
        height: 0.2em;
        border-color: var(--color-action);
        border-left-width: 1px;
        border-left-style: solid;
        border-bottom-width: 1px;
        border-bottom-style: solid;
        transform: rotate(-45deg);
      }
    }
  }
}
</style>
